import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getMemberSyncStatus, importMembersFromGestao } from "@/lib/member-sync.functions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowUpFromLine, ExternalLink, RefreshCw } from "lucide-react";
import { formatDateTimeBR } from "@/lib/datetime";

export const Route = createFileRoute("/_authenticated/admin/integracao")({
  component: AdminIntegracao,
});

function AdminIntegracao() {
  const statusFn = useServerFn(getMemberSyncStatus);
  const importFn = useServerFn(importMembersFromGestao);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["member-sync-status"],
    queryFn: () => statusFn(),
    refetchInterval: 60_000,
  });

  const importMutation = useMutation({
    mutationFn: () => importFn(),
    onSuccess: (r: any) => {
      queryClient.invalidateQueries({ queryKey: ["member-sync-status"] });
      queryClient.invalidateQueries({ queryKey: ["members"] });
      toast.success(
        `Importação concluída: ${r.created} criados, ${r.updated} atualizados, ${r.blocked ?? 0} bloqueados, ${r.revoked ?? 0} revogados, ${r.failed} falhas.`,
      );
    },
    onError: (e: any) => toast.error(e.message || "Falha ao importar cadastros"),
  });

  const webhookUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/api/public/hooks/member-sync`
      : "/api/public/hooks/member-sync";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-semibold text-primary sm:text-3xl">
          Integração de cadastros
        </h1>
        <p className="mt-1 text-sm text-muted-foreground sm:text-base">
          Conexão com a plataforma de Gestão de Cadastros: os membros aprovados lá ganham acesso
          aqui, e as alterações feitas aqui voltam para lá.
        </p>
      </div>

      {isLoading ? (
        <div
          className="min-h-[70vh] animate-pulse rounded-xl bg-secondary"
          role="status"
          aria-label="Carregando"
        />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Chave de integração
              </p>
              <p className="mt-1 font-medium">
                {data?.secretConfigured ? (
                  <Badge>Configurada</Badge>
                ) : (
                  <Badge variant="outline" className="border-gold/50 text-gold-foreground">
                    Pendente
                  </Badge>
                )}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Cadastros vinculados
              </p>
              <p className="mt-1 text-2xl font-semibold text-primary">{data?.linkedCount ?? 0}</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Plataforma de gestão
              </p>
              <a
                href={data?.baseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
              >
                Abrir <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <h2 className="font-medium text-foreground">Endpoint de recebimento</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Configure este endereço na plataforma de gestão para enviar cadastros aprovados
              (assinatura HMAC-SHA256 nos cabeçalhos <code>x-timestamp</code> e{" "}
              <code>x-signature</code>).
            </p>
            <code className="mt-2 block overflow-x-auto rounded-md bg-secondary px-3 py-2 text-xs">
              POST {webhookUrl}
            </code>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => importMutation.mutate()} disabled={importMutation.isPending}>
                {importMutation.isPending ? (
                  <RefreshCw className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <ArrowDownToLine className="mr-1.5 h-4 w-4" />
                )}
                Importar cadastros aprovados agora
              </Button>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Além do envio automático (instantâneo) pela gestão, a plataforma busca os cadastros a
              cada 5 minutos e atualiza nome, CPF, data de nascimento, foto e liberação de acesso.
              Pedidos de exclusão, cadastros removidos e dados incompletos bloqueiam o acesso aqui
              até a resolução na Gestão.
            </p>
          </div>

          <div className="rounded-xl border border-border bg-card">
            <h2 className="border-b border-border px-4 py-3 font-medium text-foreground">
              Últimas sincronizações
            </h2>
            <ul className="divide-y divide-border">
              {(data?.events ?? []).map((e: any) => (
                <li
                  key={e.id}
                  className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 truncate text-sm font-medium text-foreground">
                      {e.direction === "inbound" ? (
                        <ArrowDownToLine className="h-3.5 w-3.5 text-muted-foreground" />
                      ) : (
                        <ArrowUpFromLine className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                      {e.action}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {e.email ?? e.external_member_id ?? "—"} · {e.message ?? "sem detalhes"}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge
                      variant={e.status === "ok" ? "secondary" : "outline"}
                      className={
                        e.status === "error" ? "border-destructive/50 text-destructive" : ""
                      }
                    >
                      {e.status}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTimeBR(e.created_at)}
                    </span>
                  </div>
                </li>
              ))}
              {(!data?.events || data.events.length === 0) && (
                <li className="px-4 py-10 text-center text-sm text-muted-foreground">
                  Nenhuma sincronização registrada ainda.
                </li>
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
